import React, { useEffect } from 'react';
import type AdvancedFilter from 'talent_matching/components/filters/advanced_filter';
import { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import { DatePicker } from '@grnhse/seedling/lib/birch/components/forms/date_picker';
import { useUserContext } from 'talent_matching/contexts/user_context';
import { parseDate, formatDate } from 'talent_search/lib/utils';
import styled from 'styled-components';
import { withLocalePrefix } from 'shared/utils/translation';
import DateRangePicker from '@grnhse/seedling/lib/birch/components/forms/date_picker/date_range_picker';
import { ErrorBoundary } from 'react-error-boundary';

const t = withLocalePrefix('talent_matching.filter_row.advanced_filters');

function ErrorFallback({
  error,
  resetErrorBoundary,
}: {
  error: Error;
  resetErrorBoundary: () => void;
}) {
  console.warn('Invalid date submitted:', error);

  useEffect(() => {
    resetErrorBoundary();
  }, [resetErrorBoundary]);

  return null;
}

const Wrapper = styled.div`
  width: 100%;
  position: relative;

  .react-datepicker-wrapper {
    width: 100%;
  }

  .react-datepicker-popper {
    position: fixed;
  }

  .react-datepicker {
  }
`;

const DateRangeWrapper = styled.div`
  position: relative;

  > div {
    display: flex;

    > div {
      max-width: 50%;
    }

    .react-datepicker-wrapper {
      width: inherit;
    }

    .react-datepicker {
      width: 310px;
    }

    .react-datepicker-popper {
      position: fixed;
    }
  }
`;

type Props = {
  filter: AdvancedFilter;
  onValueChange: (newValue: string | Item | Item[] | null) => void;
};

const DateFilter = ({ filter, onValueChange }: Props) => {
  const { userDateFormat } = useUserContext();

  const [minValue, maxValue] = ((filter.value as string) || '').split(':');
  const singleDateString = minValue || maxValue;

  const selectedDate = parseDate(singleDateString, userDateFormat);
  const startDate = parseDate(minValue, userDateFormat);
  const endDate = parseDate(maxValue, userDateFormat);

  const updateFilterValue = (newDate: Date | null) => {
    let formattedDate;
    try {
      formattedDate = formatDate(newDate, userDateFormat) || '';
    } catch (_) {
      formattedDate = '';
    }

    switch (filter.operator?.value) {
      case 'equal_to':
        onValueChange(`${formattedDate}:${formattedDate}`);
        break;
      case 'on_or_before':
        onValueChange(`:${formattedDate}`);
        break;
      case 'on_or_after':
        onValueChange(`${formattedDate}:`);
        break;
    }
  };

  const updateDateRange = (start: Date | null, end: Date | null) => {
    let formattedStart;
    let formattedEnd;
    try {
      formattedStart = formatDate(start, userDateFormat) || '';
      formattedEnd = formatDate(end, userDateFormat) || '';
    } catch (_) {
      formattedStart = '';
      formattedEnd = '';
    }
    onValueChange(`${formattedStart}:${formattedEnd}`);
  };

  // Reset function to clear filter value when error occurs
  const resetFilter = () => {
    onValueChange('');
  };

  const operator = filter.operator?.value;

  useEffect(() => {
    updateFilterValue(selectedDate);
  }, [filter.operator?.value]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={resetFilter}
      resetKeys={[filter.operator?.value]}
    >
      {operator === 'between' ? (
        <DateRangeWrapper>
          <DateRangePicker
            ariaLabel={t('date')}
            onChange={updateDateRange}
            dateFormat={userDateFormat}
            selectedStart={startDate}
            selectedEnd={endDate}
          >
            <DateRangePicker.Start
              ariaLabel={t('date_start')}
              placeholder={t('date_start')}
            />
            <DateRangePicker.End
              ariaLabel={t('date_end')}
              placeholder={t('date_end')}
            />
          </DateRangePicker>
        </DateRangeWrapper>
      ) : (
        <Wrapper>
          <DatePicker
            ariaLabel={t('date')}
            placeholder={t('date')}
            onChange={updateFilterValue}
            dateFormat={userDateFormat}
            selected={selectedDate}
          />
        </Wrapper>
      )}
    </ErrorBoundary>
  );
};

export default DateFilter;
